import React, { useState } from 'react';
import Icon from '../shared/Icon';
import Avatar from '../shared/Avatar';
import Badge from '../shared/Badge';
import Toggle from '../shared/Toggle';
import useAdminList from '../../hooks/useAdminList';
import { adminFetchJson } from '../../adminFetch';
import useDebouncedValue from '../../../shared/hooks/useDebouncedValue';

// Fallback for direct use; AdminDashboard passes a product-scoped devPatchUrlFor.
const DEFAULT_DEV_PATCH_URL = userId => `/v1/admin/users/${userId}/dev`;

export default function DevIDsPage({ usersUrl, devPatchUrlFor, excludeDevs, setExcludeDevs, metricsAll, metricsReal, accent }) {
  const ac = accent || '#CC1A00';
  const [search, setSearch]           = useState('');
  const [showDevOnly, setShowDevOnly] = useState(false);
  const [saving,  setSaving]          = useState({});   // { [userId]: true } while a PATCH is in-flight
  const [errors,  setErrors]          = useState({});   // { [userId]: 'message' }
  const [overrides, setOverrides]     = useState({});   // optimistic is_dev per user

  const debouncedSearch = useDebouncedValue(search, 350);
  const { items, total, loading, loadingMore, error, hasMore, loadMore } = useAdminList(
    usersUrl,
    { search: debouncedSearch, dev_only: showDevOnly },
  );

  // Effective dev state = optimistic override if present, else the server value.
  const isDevOf = (u) => (overrides[u.user_id] !== undefined ? overrides[u.user_id] : u.is_dev);

  async function handleToggle(userId, nowDev) {
    setSaving(prev => ({ ...prev, [userId]: true }));
    setErrors(prev => ({ ...prev, [userId]: null }));
    setOverrides(prev => ({ ...prev, [userId]: nowDev }));   // optimistic
    try {
      const patchUrl = (devPatchUrlFor || DEFAULT_DEV_PATCH_URL)(userId);
      await adminFetchJson(patchUrl, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_dev: nowDev }),
      });
    } catch (err) {
      setOverrides(prev => {           // rollback
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      setErrors(prev => ({ ...prev, [userId]: err.message }));
    } finally {
      setSaving(prev => ({ ...prev, [userId]: false }));
    }
  }

  const ma = metricsAll  || {};
  const mr = metricsReal || {};
  const devCount = (ma.total_users || 0) - (mr.total_users || 0);
  const comparison = [
    { label: 'Total Users',        withDev: ma.total_users   ?? '—', withoutDev: mr.total_users   ?? '—' },
    { label: 'Total Queries',      withDev: (ma.total_queries ?? 0).toLocaleString(), withoutDev: (mr.total_queries ?? 0).toLocaleString() },
    { label: 'Avg Queries / User', withDev: ma.avg_per_user  ?? '—', withoutDev: mr.avg_per_user  ?? '—' },
    { label: 'Thumbs-Up Rate',     withDev: (ma.thumbs_up_rate ?? 0) + '%', withoutDev: (mr.thumbs_up_rate ?? 0) + '%' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 840 }}>

      {/* Global toggle card */}
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #E8EAED', padding: '20px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 5 }}>Developer Account Filter</div>
            <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.65 }}>
              Accounts marked as developer or QA are filtered from all analytics when the global toggle is active — giving clean production metrics.
              Changes are saved to the database immediately.
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <Toggle checked={excludeDevs} onChange={setExcludeDevs} accent={ac} />
            <span style={{ fontSize: 11, color: excludeDevs ? ac : '#9CA3AF', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {excludeDevs ? 'Devs excluded' : 'Devs included'}
            </span>
          </div>
        </div>
      </div>

      {/* User list with toggles */}
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #E8EAED', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '13px 20px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Icon name="shield" size={15} color={ac} />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', flex: 1 }}>
            Developer Users
          </span>
          <Badge text={devCount + ' marked'} color={ac} bg={ac + '18'} />
          <button
            onClick={() => setShowDevOnly(v => !v)}
            style={{
              fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
              border: '1px solid ' + (showDevOnly ? ac : '#E8EAED'),
              background: showDevOnly ? ac + '18' : 'white',
              color: showDevOnly ? ac : '#6B7280',
            }}
          >
            {showDevOnly ? 'Showing devs only' : 'Show devs only'}
          </button>
        </div>

        {/* Search bar */}
        <div style={{ padding: '10px 20px', borderBottom: '1px solid #F3F4F6', background: '#FAFAFA' }}>
          <div style={{ position: 'relative' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by email..."
              style={{
                width: '100%', padding: '7px 12px 7px 32px',
                border: '1px solid #E8EAED', borderRadius: 8,
                fontSize: 13, outline: 'none', fontFamily: 'inherit',
                color: '#111827', boxSizing: 'border-box', background: 'white',
              }}
            />
          </div>
        </div>

        {/* User rows */}
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {error && (
            <div style={{ padding: 28, textAlign: 'center', color: '#DC2626', fontSize: 13 }}>
              Couldn't load users. {error}
            </div>
          )}
          {!error && loading && (
            <div style={{ padding: 28, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>Loading users…</div>
          )}
          {!error && !loading && items.length === 0 && (
            <div style={{ padding: 28, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
              {debouncedSearch ? 'No users match your search.' : 'No users found.'}
            </div>
          )}
          {!error && !loading && items.map(user => {
            const isDev     = isDevOf(user);
            const isSaving  = saving[user.user_id];
            const errMsg    = errors[user.user_id];
            return (
              <div key={user.user_id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 20px', borderBottom: '1px solid #F9FAFB',
                background: isDev ? '#FAFAF9' : 'white',
                transition: 'background 0.15s',
              }}>
                <Avatar name={user.email} size={30} bg={isDev ? '#7C3AED' : '#9CA3AF'} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 600, color: '#111827',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{user.email || user.user_id}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span>{(user.query_count || 0).toLocaleString()} queries</span>
                    {isDev && <span style={{ color: '#7C3AED', fontWeight: 600 }}>• dev</span>}
                    {errMsg && <span style={{ color: '#DC2626' }}>• {errMsg}</span>}
                  </div>
                </div>
                {isSaving ? (
                  <div style={{ width: 36, display: 'flex', justifyContent: 'center' }}>
                    <div style={{ width: 14, height: 14, border: '2px solid #E5E7EB', borderTopColor: ac, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                  </div>
                ) : (
                  <Toggle
                    checked={isDev}
                    onChange={v => handleToggle(user.user_id, v)}
                    accent="#7C3AED"
                  />
                )}
              </div>
            );
          })}
          {!error && !loading && hasMore && (
            <button
              onClick={loadMore}
              disabled={loadingMore}
              style={{
                width: '100%', padding: '11px', border: 'none', borderTop: '1px solid #F3F4F6',
                background: 'white', color: ac, fontSize: 12, fontWeight: 600,
                cursor: loadingMore ? 'default' : 'pointer', fontFamily: 'inherit',
              }}
            >
              {loadingMore ? 'Loading…' : `Load more (${items.length} of ${total})`}
            </button>
          )}
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>

      {/* Impact comparison */}
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #E8EAED', padding: '18px 24px' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 14 }}>Analytics Impact — With vs. Without Devs</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {comparison.map((row, i) => (
            <div key={row.label} style={{
              padding: '10px 0', borderBottom: i < comparison.length - 1 ? '1px solid #F3F4F6' : 'none',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontSize: 13, color: '#374151' }}>{row.label}</span>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 10, color: '#9CA3AF', marginBottom: 1 }}>With devs</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#9CA3AF' }}>{row.withDev}</div>
                </div>
                <Icon name="chevRight" size={14} color="#D1D5DB" />
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: 10, color: '#9CA3AF', marginBottom: 1 }}>Without devs</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: ac }}>{row.withoutDev}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
